// src/Pages/TempProyects/index.js

import React from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    DeleteButton,
    FunctionField,
    TopToolbar,
    ExportButton,
    BulkDeleteButton,
    BulkExportButton,
    useRecordContext,
    Show,
    SimpleShowLayout,
    useDataProvider,
    useNotify,
    useRefresh
} from 'react-admin';
import { 
    Chip, 
    Link, 
    Card, 
    CardContent, 
    Typography, 
    Box,
    IconButton,
    Tooltip
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import FolderIcon from '@mui/icons-material/Folder';
import DeleteIcon from '@mui/icons-material/Delete';

// Componente para mostrar el estado con colores
const StatusField = () => {
    const record = useRecordContext();
    if (!record || !record.status) {
        return <Chip label="Sin estado" color="default" size="small" variant="outlined" />;
    }

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'success';
            case 'pending':
                return 'warning';
            case 'completed':
                return 'info';
            case 'cancelled':
            case 'inactive':
                return 'error';
            default:
                return 'default';
        }
    };

    return (
        <Chip
            label={record.status.charAt(0).toUpperCase() + record.status.slice(1)}
            color={getStatusColor(record.status)}
            size="small"
            variant="filled"
        />
    );
};

// Componente para el enlace del usuario
const UserLinkField = () => {
    const record = useRecordContext();
    
    if (!record || !record.iduser) {
        return (
            <Typography variant="body2" color="textSecondary">
                Sin usuario
            </Typography>
        );
    }

    return (
        <Link
            href={`#/users/${record.iduser}/show`}
            underline="hover"
            color="primary"
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 0.5,
                textDecoration: 'none',
                '&:hover': {
                    textDecoration: 'underline'
                }
            }}
        >
            <PersonIcon fontSize="small" />
            <Box>
                <Typography variant="body2" component="span">
                    {record.user_name || `Usuario #${record.iduser}`}
                </Typography>
                {record.user_email && (
                    <Typography variant="caption" display="block" color="textSecondary">
                        {record.user_email}
                    </Typography>
                )}
            </Box>
        </Link>
    );
};

// Componente personalizado para el nombre del proyecto
const ProjectNameField = () => {
    const record = useRecordContext();
    
    return (
        <Box>
            <Typography 
                variant="body2" 
                sx={{ 
                    fontWeight: 'medium',
                    maxWidth: '300px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}
                title={record?.nombre}
            >
                {record?.nombre || 'Sin nombre'}
            </Typography>
        </Box>
    );
};

// Componente personalizado para la fecha
const DateTimeField = () => {
    const record = useRecordContext();
    
    if (!record?.date) return null;

    const date = new Date(record.date);
    const formattedDate = date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
    });

    const timeAgo = getTimeAgo(date);

    return (
        <Box>
            <Typography variant="body2">
                {formattedDate}
            </Typography>
            <Typography variant="caption" color="textSecondary">
                {timeAgo}
            </Typography>
        </Box>
    );
};

// Función auxiliar para calcular tiempo transcurrido
const getTimeAgo = (date) => {
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Hace unos segundos';
    if (diffInSeconds < 3600) return `Hace ${Math.floor(diffInSeconds / 60)} minutos`;
    if (diffInSeconds < 86400) return `Hace ${Math.floor(diffInSeconds / 3600)} horas`;
    if (diffInSeconds < 2592000) return `Hace ${Math.floor(diffInSeconds / 86400)} días`;
    if (diffInSeconds < 31536000) return `Hace ${Math.floor(diffInSeconds / 2592000)} meses`;
    return `Hace ${Math.floor(diffInSeconds / 31536000)} años`;
};

// Botón de eliminación personalizado con confirmación
const CustomDeleteButton = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();
    const refresh = useRefresh();

    const handleDelete = async () => {
        const confirmDelete = window.confirm(
            `¿Estás seguro de que deseas eliminar el proyecto "${record.nombre || 'Sin nombre'}"?\n\nEsta acción no se puede deshacer.`
        );

        if (confirmDelete) {
            try {
                await dataProvider.delete('tempproyects', { id: record.id });
                notify('Proyecto eliminado correctamente', { type: 'success' });
                refresh();
            } catch (error) {
                notify('Error al eliminar el proyecto', { type: 'error' });
                console.error('Error deleting project:', error);
            }
        }
    };

    return (
        <Tooltip title="Eliminar proyecto">
            <IconButton
                onClick={handleDelete}
                color="error"
                size="small"
                sx={{
                    '&:hover': {
                        backgroundColor: '#ffebee'
                    }
                }}
            >
                <DeleteIcon fontSize="small" />
            </IconButton>
        </Tooltip>
    );
};

// Toolbar personalizada para la lista
const TempProyectosListActions = () => (
    <TopToolbar>
        <ExportButton />
    </TopToolbar>
);

// Acciones bulk personalizadas
const TempProyectosBulkActionButtons = () => (
    <>
        <BulkExportButton />
        <BulkDeleteButton 
            confirmTitle="Eliminar proyectos seleccionados"
            confirmContent="¿Estás seguro de que deseas eliminar estos proyectos? Esta acción no se puede deshacer."
        />
    </>
);

// Componente principal de la lista
export const TempProyectosList = () => (
    <List
        title="Proyectos Temporales"
        actions={<TempProyectosListActions />}
        perPage={25}
        sort={{ field: 'date', order: 'DESC' }}
        bulkActionButtons={<TempProyectosBulkActionButtons />}
        sx={{
            '& .RaList-content': {
                backgroundColor: '#fafafa'
            }
        }}
    >
        <Datagrid
            rowClick={false}
            sx={{
                '& .RaDatagrid-headerCell': {
                    backgroundColor: '#f5f5f5',
                    fontWeight: 'bold',
                    borderBottom: '2px solid #e0e0e0'
                },
                '& .RaDatagrid-row:hover': {
                    backgroundColor: '#f8f9fa'
                },
                '& .RaDatagrid-row:nth-of-type(even)': {
                    backgroundColor: '#fafafa'
                }
            }}
        >
            <TextField 
                source="id" 
                label="ID"
                sortable={true}
                sx={{ 
                    width: '80px',
                    '& .MuiTableCell-root': {
                        fontWeight: 'bold',
                        color: '#1976d2'
                    }
                }}
            />
            
            <FunctionField
                label="Usuario"
                render={() => <UserLinkField />}
                sortable={false}
                sx={{ minWidth: '200px' }}
            />
            
            <FunctionField
                label="Nombre del Proyecto"
                render={() => <ProjectNameField />}
                sortable={true}
                sortBy="nombre"
                sx={{ minWidth: '250px' }}
            />
            
            <FunctionField
                label="Fecha"
                render={() => <DateTimeField />}
                sortable={true}
                sortBy="date"
                sx={{ minWidth: '180px' }}
            />
            
            <FunctionField
                label="Estado"
                render={() => <StatusField />}
                sortable={true}
                sortBy="status"
                sx={{ minWidth: '120px' }}
            />
            
            <FunctionField
                label="Acciones"
                render={() => <CustomDeleteButton />}
                sortable={false}
                sx={{ width: '100px' }}
            />
        </Datagrid>
    </List>
);

// Componente separado para el botón de eliminación en la vista detallada
const CustomDeleteButtonShow = () => {
    const record = useRecordContext();
    const dataProvider = useDataProvider();
    const notify = useNotify();

    const handleDelete = async () => {
        const confirmDelete = window.confirm(
            `¿Estás seguro de que deseas eliminar el proyecto "${record.nombre || 'Sin nombre'}"?\n\nEsta acción no se puede deshacer.`
        );

        if (confirmDelete) {
            try {
                await dataProvider.delete('tempproyects', { id: record.id });
                notify('Proyecto eliminado correctamente', { type: 'success' });
                window.history.back();
            } catch (error) {
                notify('Error al eliminar el proyecto', { type: 'error' });
            }
        }
    };

    return (
        <IconButton
            onClick={handleDelete}
            color="error"
            sx={{
                '&:hover': {
                    backgroundColor: '#ffebee'
                }
            }}
        >
            <DeleteIcon />
        </IconButton>
    );
};

// Componente separado para el chip de estado en la vista detallada
const StatusChipShow = () => {
    const record = useRecordContext();
    if (!record || !record.status) return null;

    const getStatusColor = (status) => {
        switch (status?.toLowerCase()) {
            case 'active':
                return 'success';
            case 'pending':
                return 'warning';
            case 'completed':
                return 'info';
            case 'cancelled':
                return 'error';
            default:
                return 'default';
        }
    };

    return (
        <Chip
            label={record.status.charAt(0).toUpperCase() + record.status.slice(1)}
            color={getStatusColor(record.status)}
            variant="filled"
            size="medium"
        />
    );
};

// Componente separado para mostrar información del usuario en la vista detallada
const UserInfoShow = () => {
    const record = useRecordContext();
    
    if (!record?.iduser) {
        return (
            <Typography variant="body1" color="textSecondary">
                Sin usuario asociado
            </Typography>
        );
    }
    
    return (
        <Link
            href={`#/users/${record.iduser}/show`}
            sx={{ 
                textDecoration: 'none',
                '&:hover': { textDecoration: 'underline' }
            }}
        >
            <Typography variant="h6" color="primary">
                {record.user_name || `Usuario #${record.iduser}`}
            </Typography>
            {record.user_email && (
                <Typography variant="body2" color="textSecondary">
                    {record.user_email}
                </Typography>
            )}
        </Link>
    );
};

// Toolbar para la vista detallada
const TempProyectosShowActions = () => (
    <TopToolbar>
        <CustomDeleteButtonShow />
    </TopToolbar>
);

// Componente de vista detallada
export const TempProyectosShow = () => {
    return (
        <Show actions={<TempProyectosShowActions />} title="Detalle del Proyecto Temporal">
            <SimpleShowLayout>
                <Card sx={{ mb: 2, boxShadow: 2 }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" mb={3}>
                            <FolderIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
                            <Typography variant="h5" component="h2" fontWeight="bold">
                                Información del Proyecto
                            </Typography>
                        </Box>
                        
                        <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={3}>
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    ID del Proyecto
                                </Typography>
                                <Typography variant="h6" color="primary" fontWeight="bold">
                                    #<TextField source="id" />
                                </Typography>
                            </Box>
                            
                            <Box>
                                <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                    Estado
                                </Typography>
                                <StatusChipShow />
                            </Box>
                        </Box>
                        
                        <Box mt={3}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Nombre del Proyecto
                            </Typography>
                            <Typography variant="h6" fontWeight="medium">
                                <TextField source="nombre" />
                            </Typography>
                        </Box>
                    </CardContent>
                </Card>

                <Card sx={{ mb: 2, boxShadow: 2 }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" mb={3}>
                            <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
                            <Typography variant="h5" component="h2" fontWeight="bold">
                                Usuario Asociado
                            </Typography>
                        </Box>
                        
                        <UserInfoShow />
                    </CardContent>
                </Card>

                <Card sx={{ boxShadow: 2 }}>
                    <CardContent>
                        <Box display="flex" alignItems="center" mb={3}>
                            <CalendarTodayIcon sx={{ mr: 1, color: 'primary.main', fontSize: 28 }} />
                            <Typography variant="h5" component="h2" fontWeight="bold">
                                Información Temporal
                            </Typography>
                        </Box>
                        
                        <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                            Fecha de Creación
                        </Typography>
                        <DateField 
                            source="date" 
                            showTime
                            locales="es-ES"
                            options={{
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                            }}
                            sx={{
                                fontSize: '1.1rem',
                                fontWeight: 'medium'
                            }}
                        />
                    </CardContent>
                </Card>
            </SimpleShowLayout>
        </Show>
    );
};

// Exportar el icono para usar en la configuración del recurso
export const TempProyectosIcon = AccountTreeIcon;

// Configuración del recurso (para usar en App.js)
export const tempProyectosResource = {
    name: "tempproyects",
    list: TempProyectosList,
    show: TempProyectosShow,
    icon: TempProyectosIcon,
    options: { label: 'Proyectos Temporales' }
};